import { useMutation, useQuery } from '@tanstack/react-query'

import api from '@/admin/lib/api'

// Generic bulk-import client for the descriptor-driven engine. One set of hooks
// for every target (registration, claim:<schema>, hoa_claim) - the target token
// is all that differs. See docs/bulk-import-engine.md.

export interface BulkImportColumn {
  code_name: string
  display_name: string
  field_type: string
  required?: boolean
  description?: string
  options?: Array<{ value: string; label?: string }>
  configuration_data?: Record<string, unknown>
}

export interface BulkImportUmbrella {
  parent_claim_schema_key: string
  child_claim_schema_key: string
  parent_columns: BulkImportColumn[]
}

export interface BulkImportGroup {
  /** Group-level fields filled once for the whole upload (community + product). */
  group_columns: BulkImportColumn[]
}

export interface BulkImportRowError {
  field: string
  message: string
}

export interface BulkImportPreviewRow {
  row: number
  status: 'ok' | 'failed'
  resolved: Record<string, string>
  errors: BulkImportRowError[]
}

export interface BulkImportValidateResult {
  total: number
  ok: number
  failed: number
  mapping: Record<string, string>
  unmapped_headers: string[]
  rows: BulkImportPreviewRow[]
}

export interface BulkImportJobResultRow {
  row: number
  status: string
  produced: Record<string, string>
  errors: BulkImportRowError[]
}

export interface BulkImportJob {
  job_id: string
  target: string
  mode: 'live' | 'backfill'
  status: 'pending' | 'processing' | 'completed' | 'failed'
  total: number
  created: number
  failed: number
  skipped: number
  error: string
  results: BulkImportJobResultRow[]
  finished_at: string | null
}

export interface BulkImportTarget {
  token: string
  label: string
}

const enc = (target: string) => encodeURIComponent(target)

export function useBulkImportTargets(enabled: boolean) {
  return useQuery<{ targets: BulkImportTarget[] }>({
    queryKey: ['bulk-import-targets'],
    queryFn: () => api.get('/bulk-import/targets/').then((r) => r.data),
    enabled,
    staleTime: 60_000,
  })
}

export function useBulkImportSchema(target: string, enabled: boolean) {
  return useQuery<{
    target: string
    columns: BulkImportColumn[]
    column_map: Record<string, string>
    umbrella: BulkImportUmbrella | null
    group: BulkImportGroup | null
  }>({
    queryKey: ['bulk-import-schema', target],
    queryFn: () => api.get(`/bulk-import/${enc(target)}/schema/`).then((r) => r.data),
    enabled: enabled && !!target,
    staleTime: 60_000,
  })
}

export function useBulkImportValidate(target: string) {
  return useMutation<
    BulkImportValidateResult,
    Error,
    { file: File; mapping?: Record<string, string> }
  >({
    mutationFn: ({ file, mapping }) => {
      const body = new FormData()
      body.append('file', file)
      if (mapping && Object.keys(mapping).length > 0) {
        body.append('mapping', JSON.stringify(mapping))
      }
      return api
        .post(`/bulk-import/${enc(target)}/validate/`, body)
        .then((r) => r.data)
    },
  })
}

export function useBulkImportCommit(target: string) {
  return useMutation<
    { job_id: string; status: string; total: number },
    Error,
    {
      file: File
      mode: 'live' | 'backfill'
      mapping?: Record<string, string>
      params?: Record<string, unknown>
    }
  >({
    mutationFn: ({ file, mode, mapping, params }) => {
      const body = new FormData()
      body.append('file', file)
      body.append('mode', mode)
      if (mapping && Object.keys(mapping).length > 0) {
        body.append('mapping', JSON.stringify(mapping))
      }
      if (params && Object.keys(params).length > 0) {
        body.append('params', JSON.stringify(params))
      }
      return api.post(`/bulk-import/${enc(target)}/commit/`, body).then((r) => r.data)
    },
  })
}

export function useSaveBulkImportColumnMap(target: string) {
  return useMutation<
    { target: string; column_map: Record<string, string> },
    Error,
    { mappings: Record<string, string> }
  >({
    mutationFn: ({ mappings }) =>
      api
        .post(`/bulk-import/${enc(target)}/column-map/`, { mappings })
        .then((r) => r.data),
  })
}

const isTerminal = (s?: string) => s === 'completed' || s === 'failed'

export function useBulkImportJob(jobId: string | null) {
  return useQuery<BulkImportJob>({
    queryKey: ['bulk-import-job', jobId],
    queryFn: () => api.get(`/bulk-import/jobs/${jobId}/`).then((r) => r.data),
    enabled: !!jobId,
    // Poll until the job reaches a terminal state.
    refetchInterval: (query) =>
      isTerminal(query.state.data?.status) ? false : 1500,
  })
}
